
import { fetchRequest } from "./api/api.js";
import { getUTCDateFromString, getTodayUTC } from "./utils/date-utils.js";
import { getBookingStateProp } from "./state.js";
import { AvailabilityType } from "./enums/availability-type.js";

var startSelect = -1;
var lastMouseover = -1;
var months = 2;

/*
som - Start of month
eom - end of month
eow - End of Week
sow - Start of Week
b - booked
a - available
p - past 
f - filler
c - closed
s - starting day
*/
const CellState = Object.freeze({
	StartOfMonth: 'som',
	EndOfMonth: 'eom',
	StartOfWeek: 'sow',
	EndOfWeek: 'eow',
	Filler: 'f',
	Past: 'p',
	Booked: 'b',
	Available: 'a',
	StartAvailable: 's',
	Selected: 'selection'
});


/**
 * An interface for working with the legacy calendar, still using JQuery
 */
export const calendar = (() => {
    let startCallback = null;
    let endCallback = null;

    return {
		// Attach callbacks for the calendar prev/next buttons
		init: () => {
			const prevBtns = document.querySelectorAll('#atb_prev, #atb_prev_mobile');
			const nextBtns = document.querySelectorAll('#atb_next, #atb_next_mobile');

			prevBtns.forEach(btn => {
				btn.addEventListener('click', prevMonth)
			});
			nextBtns.forEach(btn => {
				btn.addEventListener('click', nextMonth)
			});			
		},

		// Generate a calendar, given a start day and lenth of stay - options
		generate: (
			startDateStr = '', 
			lengthOfStay = 1, 
			options = { 
				usingPreset             : false,
				findFirstAvailable      : false,
				// When true, the first available start day returned by the backend is selected immediately.
				autoSelectFirstAvailable: false,
				onFirstAvailableNotFound: null,
				partySize               : 0
			}) => {
			// Call the existing generateCalendar function
				generateCalendar(
					startDateStr,
					lengthOfStay,
					options.usingPreset,
					options.findFirstAvailable,
					getCalendarRequestPartySize(options.partySize),
					options.autoSelectFirstAvailable,
					options.onFirstAvailableNotFound
				);
		},

		// Update all arrival text
		updateBtnText: (arrivalDateText, departureDateText, period) => {
			const calendarBtnEl = document.querySelector('#atb-calendar-btn');
			const [ arrivalEl, departureEl, periodEl ]       = Array.from(calendarBtnEl.querySelectorAll('span'));

			const nightsInflection = period > 1 ? "nights" : "night";
			const periodText       = `(${period} ${nightsInflection})`;

			// change the departure text
			arrivalEl.innerText   = `${arrivalDateText}`;
			departureEl.innerText = `- ${departureDateText}`;
			periodEl.innerText    = `${periodText}`;

			textEls.forEach(textEl => {
				textEl.classList.remove('d-none');
			});
		},

		// Update the departure text only
		updateBtnDepartureText: (departureDateText) => {
			const calendarBtnEl = document.querySelector('#atb-calendar-btn');
			const textEls = [
				calendarBtnEl.querySelector('#atb-calendar-btn-departure-text'),
				calendarBtnEl.querySelector('#atb-calendar-btn-period-text')
			]

			// change the departure text
			textEls[0].innerText = `- ${departureDateText}`;

			textEls.forEach(textEl => {
				textEl.classList.remove('d-none');
			});
		},

		// Update the period ("x days") text on the button
		updateBtnPeriodText: (period) => {
			// Update the calendar buttons departure text			
			const nightsInflection = period > 1 ? "nights" : "night";
			const periodText       = `(${period} ${nightsInflection})`;
			
			const el = document.querySelector('#atb-calendar-btn #atb-calendar-btn-period-text');
			if (el) el.textContent = periodText;
		},

		// Reset the calendar button text
		resetBtnText: () => {
			// Reset calendar button texts
			const calendarBtnEl = document.querySelector('#atb-calendar-btn');
			const textEls       = calendarBtnEl.querySelectorAll('span');

			textEls.forEach(textEl => 
				textEl.innerText = ""
			);				
			textEls[0].innerText = "Click to open calendar";			
		},

		// Wipes the calendar and optionally hides the calendar button by hiding the whole wrapper
        destroy: ({doHideWrapper = false} = {}) => wipeCalendar(doHideWrapper),

		// Set a callback for when user clicks on start date
        onStartSelected: (callback) => {
            startCallback = callback;
        },

		// Set a callback for when user clicks on end date
        onEndSelected: (callback) => {
            endCallback = callback;
        },

		// A trigger to be used in "startDaySelected"
        _triggerStart: (date) => {
            if (typeof startCallback === 'function') startCallback(date);
        },

		// A trigger to be used in "endDaySelected"
        _triggerEnd: (period) => {
            if (typeof endCallback === 'function') endCallback(period);
        }
    };
})();


// TODO: Refactor the contents of this - split into function that gets endDate from start date and period/lengthOfStay
// then call from getCalendar and eliminate this function alltogether
const generateCalendar = (	
	startDateStr = '', 
	lengthOfStay = 1, 
	usingPreset = false,
	findFirstAvailable = false,
	partySize = 0,
	autoSelectFirstAvailable = false,
	onFirstAvailableNotFound = null
) => {
	let startDate = startDateStr != '' 
		? getUTCDateFromString(startDateStr) 
		: getTodayUTC();

	let startStr = startDate.toISOString().substring(0, 10);
	let endStr = '';
	lengthOfStay = parseInt(lengthOfStay, 10);

	if (startDateStr) {
		let endDate = new Date(startDate);
		endDate.setDate(endDate.getDate() + lengthOfStay);
		endStr = endDate.toISOString().substring(0, 10);
	}

	getCalendar(
		startStr,
		endStr,
		usingPreset,
		findFirstAvailable,
		partySize,
		autoSelectFirstAvailable,
		onFirstAvailableNotFound
	);
}

const buildDateMap = (startDateStr, endDateStr) => {
	const dateMap = {};
	const current = new Date(startDateStr);
	const end     = new Date(endDateStr);

	while (current <= end) {
		const iso = current.toISOString().substring(0, 10); // 'YYYY-MM-DD'
		dateMap[iso] = true;
		current.setUTCDate(current.getUTCDate() + 1);
	}

	return dateMap;
}


const getCalendar = async (
	startDate, 
	endDate, 
	usingPreset = false,
	findFirstAvailable = false,
	partySize = 0,
	autoSelectFirstAvailable = false,
	onFirstAvailableNotFound = null
) => {
	let doShowDiagonals = 0;
	let doShowProvisionals = 0;
	let availabilityType = 1; 
	const selectedUnitId = parseIntb10($('#atb-unit').attr('data-selected-id'));
	let unit = selectedUnitId > 0 ? selectedUnitId : getBookingStateProp('unit');
	
	const calendarData = await getCalendarData(startDate, unit, 2, findFirstAvailable, partySize);
	
	if (calendarData.good) {

		// if the response is good, we can confidently destructure 
		const { 
			cal:calendarArray, 
			prev, 
			next 
		} = calendarData;

		let rawCalendarHTML = '';

		$('#atb_prev').attr('data-month', prev);
		$('#atb_next').attr('data-month', next);
		$('#atb_prev_mobile').attr('data-month', prev);
		$('#atb_next_mobile').attr('data-month', next);

		let subCalendar = 1;
		let cellNum = 0;
		let lastType = '';
		let lastDiscounted = 0;
		let last = 1;

		const bookingDatesMap = usingPreset ? buildDateMap(startDate, endDate) : null;

		calendarArray.forEach(item => {				
			const cellState = item[0];
			const label = item[1];
			const date = item[2];

			if (cellState == CellState.StartOfMonth) {					
				let monthName = capitaliseFirstLetter(label);					

				$('#monthname' + subCalendar).html(monthName);
				rawCalendarHTML += renderCalendarDaysHeading();
				return;
			}

			if (cellState == CellState.StartOfWeek) {
				rawCalendarHTML += '<div class="week_start">';
				return;
			}

			if (cellState == CellState.EndOfWeek) {
				rawCalendarHTML += '</div>';
				return;
			}

			if (cellState == CellState.Filler) {
				rawCalendarHTML += renderCalCellState(cellState);				
				return;
			}

			if (bookingDatesMap && bookingDatesMap[date]) {					
				lengthOfStay = '';
				if (item[3] !== null) {
					const losArr = item[3];
					losArr.forEach(({ duration }) => {
						lengthOfStay += lengthOfStay.length > 0 
							? `,${duration}` 
							: duration;
					});
				}

				rawCalendarHTML += renderCalCellState(CellState.Selected, label, cellNum, date, lengthOfStay);
				cellNum++;
				return;
			}

			if (cellState == CellState.Past) {
				rawCalendarHTML += renderCalCellState(cellState, label);
				last = 0;
				// cellNum++;
				return;
			}

			if (cellState == CellState.Closed) {
				// TODO: refactor this, av_type 4 is too vague
				if (last == 1 && availabilityType < AvailabilityType.Time) {
					rawCalendarHTML += '<div id="cal_' + cellNum + '" class="cell available last_cell"><div class="background_border"></div><div class="date_number">' + label + '</div></div>';					
				} else {
					rawCalendarHTML += renderCalCellState(cellState, label, cellNum,);
				}
				cellNum++;
				last = 0;
				return;
			}

			if (cellState == CellState.Booked) {
				if (availabilityType < AvailabilityType.Time) {
					if (last === 1) {
						const isAvailable = doShowDiagonals === 1;
						const isProvisional = (
							doShowDiagonals &&
							doShowProvisionals &&
							parseInt(item[7], 10) > 0 &&
							availabilityType === AvailabilityType.Standard // standard
						);

						let classes = ['cell', isAvailable ? 'available' : 'booked'];
						if (isAvailable) {
							if (isProvisional) {
								classes.push('last_cell_provisional');
								lastType = 'provisional';
							} else {
								classes.push('last_cell');
								lastType = 'booked';
							}
						}

						rawCalendarHTML += `
							<div id="cal_${cellNum}" class="${classes.join(' ')}" data-date="${date}">
								<div class="background_border"></div>
								<div class="date_number">${label}</div>
							</div>
						`;

					} else {
						const isProvisional = (
							doShowProvisionals === 1 &&
							parseInt(item[7], 10) > 0 &&
							availabilityType === AvailabilityType.Standard
						);

						let classes = ['cell'];

						if (doShowProvisionals === 1) {
							if (isProvisional) {
								classes.push('provisional');

								if (lastType === 'booked' && doShowDiagonals === 1) {
									classes.push('first_cell_booked');
								}

								lastType = 'provisional';
							} else {
								classes.push('booked');

								if (lastType === 'provisional' && doShowDiagonals) {
									classes.push('first_cell_provisional');
								}

								lastType = 'booked';
							}
						} else {
							classes.push('booked');
							lastType = 'booked';
						}

						rawCalendarHTML += `
							<div class="${classes.join(' ')}" data-date="${date}">
								<div class="background_border"></div>
								<div class="date_number">${label}</div>
							</div>
						`;
					}
				} else {
					// Fallback for availabilityType >= 4
					rawCalendarHTML += `
						<div class="cell booked" data-date="${date}">
							<div class="background_border"></div>
							<div class="date_number">${label}</div>
						</div>
					`;
					lastType = 'booked';
					
				}
				cellNum++;
				last = 0;
				return;
			}

			if (cellState == CellState.Available) {
				rawCalendarHTML += '<div id="cal_' + cellNum + '" class="cell';
				if (parseInt(item[4], 10) > 0 && availabilityType == AvailabilityType.Time) {
					rawCalendarHTML += ' limited';
				} else {
					rawCalendarHTML += ' available';
				}

				if (last === 0 && availabilityType < 4 && doShowDiagonals == 1) {
					if (lastType == 'provisional') {
						rawCalendarHTML += ' first_cell_provisional';
					} else {
						rawCalendarHTML += ' first_cell';
					}
				}

				if (parseInt(item[8], 10) > 0) {
					rawCalendarHTML += ' discounted';
					if (lastDiscounted === 0 && doShowDiagonals == 1) {
						rawCalendarHTML += ' first_cell_discounted';
					}
				} else {
					if (lastDiscounted == 1 && doShowDiagonals == 1) {
						rawCalendarHTML += ' discounted last_cell_discounted';
					}
				}
				rawCalendarHTML += '" data-date="' + date + '"><div class="background_border"></div><div class="date_number">' + label + '</div></div>';
				last = 1;
				cellNum++;
				return;
			}

			if (cellState == CellState.StartAvailable) {
				calendarRates = '';
				lengthOfStay = '';				

				const losArr = item[3];
				let tooltip = '';

				// loop over and concatenate all rates, durations and build tooltip strings
				losArr.forEach(({ rate, duration }) => {						
					calendarRates += calendarRates.length > 0 
						? `,${rate}`
						: rate;

					tooltip += `${duration} nights &pound;${rate}\n`;
					lengthOfStay += lengthOfStay.length > 0 
						? `,${duration}` 
						: duration;
				});
				
				rawCalendarHTML += '<div id="cal_' + cellNum + '" class="cell';
				if (calendarRates !== '') {
					rawCalendarHTML += ' start';
				}

				rawCalendarHTML += calendarRates !== '' 
					? ' start'
					: '';


				rawCalendarHTML += parseInt(item[4], 10) > 0 
									&& availabilityType == AvailabilityType.Time
									? ' limited'
									: ' available';

				if (last === 0 && availabilityType < 4 && doShowDiagonals == 1) {
					if (lastType == 'provisional') {
						rawCalendarHTML += ' first_cell_provisional';
					} else {
						rawCalendarHTML += ' first_cell';
					}
				}

				if (parseInt(item[8], 10) > 0) {
					rawCalendarHTML += ' discounted';
					if (lastDiscounted == 0 && doShowDiagonals == 1) {
						rawCalendarHTML += ' first_cell_discounted';
					}
				} else {
					if (lastDiscounted == 1 && doShowDiagonals == 1) {
						rawCalendarHTML += ' discounted last_cell_discounted';
					}
				}

				rawCalendarHTML += '" data-date="' + date + '" data-los="' + lengthOfStay + '" ' + getSelectableDateAttrs(date, 'Select arrival date') + '>';
				rawCalendarHTML += '<div class="background_border"></div><div class="date_number">' + item[1] + '</div></div>';
				last = 1;
				cellNum++;
				return;
			}

			if (item[0] == 'eom') {
				$('#calender_content' + subCalendar).children().remove();
				$('#calender_content' + subCalendar).html(rawCalendarHTML);
				rawCalendarHTML = '';
				subCalendar++;
				return;
			}
			lastDiscounted = parseInt(item[8], 10);
		});

		if (usingPreset)
			reset2()
		else
			setupFirstClick();

		if (autoSelectFirstAvailable) {
			const wasDateSelected = autoSelectFirstAvailableDate(calendarArray);
			if (!wasDateSelected && typeof onFirstAvailableNotFound === 'function')
				onFirstAvailableNotFound();
		}

	} else {

		// TODO: In the future, we should not be using alert - comment this back in after fixing the issue with events activating the av calendar
		// alert('Refresh this page');
	}

	function autoSelectFirstAvailableDate(calendarArray) {
		// Missing-date presets need the same side effects as a user clicking the first available date.
		const firstAvailable = calendarArray.find(item => item[0] === CellState.StartAvailable && item[2]);
		if (!firstAvailable) return false;

		const arrivalDate = firstAvailable[2];
		const losOptions = firstAvailable[3] || [];
		const period = parseInt(losOptions[0]?.duration, 10) || 1;
		const cell = $(`.cell.start[data-date="${arrivalDate}"]`).first();

		if (!cell.length) return false;

		$('.start').addClass('was_start').removeClass('start').off();
		cell.addClass('selection');
		startSelect = parseInt(cell.prop('id').replace('cal_', ''), 10);

		startDayClicked(arrivalDate, 29, period);
		return true;
	}
}

function wipeCalendar(doHideWrapper = true) {
	startSelect = -1;
	$('#calender_content1').children().remove();
	$('#calender_content2').children().remove();
	
	injectCalendarSkeleton();
	if (doHideWrapper)
		$('#atb-calendar-row').hide();
}

function injectCalendarSkeleton() {
	const template = document.querySelector('#calendar-skeleton-template');

    if (!template) {
        console.warn('Calendar skeleton template not found.');
        return;
    }

    // Clone and inject into each wrapper
    [1, 2].forEach((num) => {
        const wrapper = document.querySelector(`#calendar-wrapper-${num}`);

		if (!wrapper) return;
        
		// Clear existing children first if needed
		wrapper.innerHTML = '';

		// Clone template content
		const clone = template.content.cloneNode(true);
		wrapper.appendChild(clone);

		// set calendar_content id
		const contentDiv = wrapper.querySelector('.atb_calendar_content #calender_content');
		const monthNameDiv = wrapper.querySelector('.atb_calendar_content .atb_calendar_month');

		if (monthNameDiv) monthNameDiv.id = `monthname${num}`;
		else console.warn(`monthname div not found in wrapper #calendar-wrapper-${num}.`);

		if (contentDiv) contentDiv.id = `calender_content${num}`;
		else console.warn(`Calendar content div not found in wrapper #calendar-wrapper-${num}.`);		
    });
}

// TODO: can probably go
// function updateCalendarButtonPeriodText(period) {
// 	// Update the calendar buttons departure text			
// 	const nightsInflection = period > 1 ? "nights" : "night";
// 	const periodText = `(${period} ${nightsInflection})`;
	
// 	$('#atb-calendar-btn #atb-calendar-btn-period-text').text(periodText);
// }

function getCalendarRequestPartySize(requestedPartySize = 0) {
	const availabilityType = parseInt(getBookingStateProp('availability_type'), 10);

	if (availabilityType === AvailabilityType.Occupancy) {
		return parseInt(requestedPartySize, 10)
			|| parseInt(getBookingStateProp('party_size'), 10)
			|| 0;
	}

	if (availabilityType === AvailabilityType.Booking) {
		return 1;
	}

	return 0;
}

function prevMonth() {
	reset();

	const prevMonth = $('#atb_prev').attr('data-month');
	const partySize = getCalendarRequestPartySize();
	// const usingPreset = $('#atb-preset').attr('data-mode') == 'preset';
	if (prevMonth) {
		// clearSelection();
		getCalendar(prevMonth, '', false, false, partySize);
	} else {
		alert('Please refresh this page');
	}
}

function nextMonth() {
	reset();

	// get the data
	const nextMonth = $('#atb_next').attr('data-month');
	const partySize = getCalendarRequestPartySize();
	// const usingPreset = $('#atb-preset').attr('data-mode') == 'preset';
	
	if (nextMonth) {
		// clearSelection();
		getCalendar(nextMonth, '', false, false, partySize);
	} else {
		alert('Please refresh this page');
	}
}

function setupFirstClick() {
	// attach click event to all start cells
	
	const startCells = $('.start');
	startCells.attr('tabindex', '0');
	startCells.attr('role', 'button');

	startCells.off('click');
	startCells.on('click', function () {
        // add first selection
		// get clicked cell 
		let cell = $(this);
        cell.addClass('selection');
        if (startSelect == -1) {
            startCells.addClass('was_start');
            startCells.removeClass('start');
            startCells.off();

			// Remove the "cal_" part of the id and store it in global variable 'startSelect'
            startSelect = cell.prop('id').substr(4) - 0;
			
            let max = 29;
            for (let n = 1; n <= max; n++) {
                if ($('#cal_' + (startSelect + n)).hasClass('first_cell') && max == 29) {
                    max = n;
                }
            }

			const arrivalDate = cell.data('date');
			startDayClicked(arrivalDate, max);
		}
	});

	startCells.off('keydown');
	startCells.on('keydown', triggerCellClickFromKeyboard);
}

const setupSecondClick = () => {
	$('.good_los').each(function () {
		$(this).attr('tabindex', '0');
		$(this).attr('role', 'button');
		$(this).attr('aria-label', `Select departure date ${formatAccessibleDate($(this).data('date'))}`);

		$(this).off('mouseover');
		$(this).on('mouseover', function () {
			lastMouseover = $(this).prop('id').substr(4) - 0;
			for (let n = startSelect; n <= lastMouseover; n++) {
				$('#cal_' + n).addClass('selection');
			}
		});

		$(this).off('mouseout');
		$(this).on('mouseout', function () {
			if (lastMouseover != -1) {
				for (let n = startSelect + 1; n <= lastMouseover; n++) {
					$('#cal_' + n).removeClass('selection');
				}
			}
		});

		console.log(startSelect);
		$(this).off('click');
		$(this).on('click', function () {
			let lengthOfStay = ($(this).prop('id').substr(4) - 0) - startSelect;
			endDayClicked(lengthOfStay);
			/*$('.good_los').each(function () {
				$(this).off();
				$(this).removeClass('good_los start');
			});*/
		});

		$(this).off('keydown');
		$(this).on('keydown', triggerCellClickFromKeyboard);
	});
}

function resetCommon() {
	startSelect = -1;
	$('.good_los').each(function () {
		$(this).removeClass('good_los');
		$(this).off();
	});
	$('.was_start').each(function () {
		$(this).removeClass('was_start');
		$(this).addClass('start');
	});
	setupFirstClick();
}

function reset2() {
	resetCommon();
}

function reset() {
	resetCommon();
	clearSelection();
}

function clearSelection() {
	// remove selected dates
	$('#atb_calendar_container .cell.selection').removeClass('selection');

	// Clear the departure date and period number in the booking summary
	$('#atb-summary-departure-date').text('');
	$('#atb-summary-period').text('');

	// Clear the hidden period input
	$('#atb-period').val(0);

	// Hide the validation message on the period if it's active
	$('#atb-period-invalid').removeClass('d-block');

	// remove document selection and cursor hightlights
	if (document.selection) {
		document.selection.empty();
	} else if (window.getSelection) {
		window.getSelection().removeAllRanges();
	}
}


const renderCalendarDaysHeading = () => {
	return `
		<div class="days">
			<div class="cell">S</div>
			<div class="cell">M</div>
			<div class="cell">T</div>
			<div class="cell">W</div>
			<div class="cell">T</div>
			<div class="cell">F</div>
			<div class="cell">S</div>
		</div>
	`;
}

const triggerCellClickFromKeyboard = (event) => {
	if (event.key !== 'Enter' && event.key !== ' ') return;

	event.preventDefault();
	$(event.currentTarget).trigger('click');
}

const getSelectableDateAttrs = (dateString, actionText) => {
	return `tabindex="0" role="button" aria-label="${actionText} ${formatAccessibleDate(dateString)}"`;
}

const formatAccessibleDate = (dateString) => {
	const date = new Date(`${dateString}T00:00:00Z`);
	if (Number.isNaN(date.getTime())) return dateString;

	return date.toLocaleDateString('en-GB', {
		weekday : 'long',
		day     : 'numeric',
		month   : 'long',
		year    : 'numeric',
		timeZone: 'UTC'
	});
}

const renderBookedCell = () => {
	if (availabilityType < 4) {
		if (last == 1) {
			rawCalendarHTML += '<div id="cal_' + cellNum + '" class="cell ';
			if (doShowDiagonals == 1) {
				rawCalendarHTML += 'available';
			} else {
				rawCalendarHTML += 'booked';
			}
			if (doShowDiagonals == 1) {
				if (doShowProvisionals == 1 && parseInt(item[7], 10) > 0 && availabilityType == AvailabilityType.Standard) {
					rawCalendarHTML += ' last_cell_provisional';
					lastType = 'provisional';
				} else {
					rawCalendarHTML += ' last_cell';
					lastType = 'booked';
				}
			}
			rawCalendarHTML += '" data-date="' + date + '"><div class="background_border"></div><div class="date_number">' + label + '</div></div>';
		} else {
			if (doShowProvisionals == 1) {
				if (doShowProvisionals == 1 && parseInt(item[7], 10) > 0 && availabilityType == AvailabilityType.Standard) {
					rawCalendarHTML += '<div class="cell';
					if (lastType == 'booked' && doShowDiagonals == 1) {
						rawCalendarHTML += ' provisional first_cell_booked';
					} else {
						rawCalendarHTML += ' provisional';
					}
					rawCalendarHTML += '" data-date="' + date + '"><div class="background_border"></div><div class="date_number">' + item[1] + '</div></div>';
					lastType = 'provisional';
				} else {
					rawCalendarHTML += '<div class="cell';
					if (lastType == 'provisional' && doShowDiagonals == 1) {
						rawCalendarHTML += ' booked first_cell_provisional';
					} else {
						rawCalendarHTML += ' booked';
					}
					rawCalendarHTML += '" data-date="' + date + '"><div class="background_border"></div><div class="date_number">' + item[1] + '</div></div>';
					lastType = 'booked';
				}
			} else {
				rawCalendarHTML += '<div class="cell booked" data-date="' + date + '"><div class="background_border"></div><div class="date_number">' + item[1] + '</div></div>';
				lastType = 'booked';
			}
		}
		cellNum++;
	} else {
		rawCalendarHTML += '<div class="cell booked" data-date="' + date + '"><div class="background_border"></div><div class="date_number">' + item[1] + '</div></div>';
		lastType = 'booked';
	}

	return `<div class="cell "><div class="background_border"></div><div class="date_number">${contents}</div></div>`;
}

const renderCalCellState = (cellState, contents = '&nbsp;', cellNum, dateString, losString) => {
	let stateClass = '';
	let calId = `id="cal_${cellNum}" `;
	let dataDate = `data-date="${dateString}"`;
	let dataLengthOfStay = `data-los="${losString}"`;
	switch (cellState) {
		case CellState.Past:
			stateClass = 'past';
			break;
		case CellState.Filler:
			stateClass = 'filler';
			break;
		case CellState.Available:
			stateClass = 'available';
			break;
		case CellState.Closed:
			stateClass = 'closed';
			break;
		case CellState.Booked:
			stateClass = 'booked';
			break;
		case CellState.Selected:
			stateClass = 'available start selection';
			break;
		default:
			stateClass = '';
			break;
	}
	return `<div ${calId}class="cell ${stateClass}" ${dataDate} ${dataLengthOfStay}><div class="background_border"></div><div class="date_number">${contents}</div></div>`;
}

const getCalendarData = async (startDate, unitId, months, findFirstAvailable, partySize = 0) => {
    try {
		//console.log('partySize', partySize);
		toggleLoadingOverlay(true);
		// TODO: the av_calendar function requires a get request - may change it to use post instead for consistency
        const response = await fetchRequest('/checkout_calendar/av_calendar/', {
            start_date: startDate,
            dow: 0,
            unit: unitId,
            months: months,
			find_first_available_date: findFirstAvailable ? 1 : 0,
			party_size: partySize // NEW
        }, 'get');

        const dataText = await response.text(); // because original used $.parseJSON
        const jsonData = JSON.parse(dataText);
		console.log('Calendar data for unit:', unitId, jsonData);
        return jsonData;
    } catch (error) {
        console.error('Error fetching calendar data:', error);
        return null;
    } finally {
		toggleLoadingOverlay(false);
	}
}


function startDayClicked(arrivalDate, max, autoPeriod = 0) {
    	// clear previous selection (if any have been made)
	const numDaysSelected = parseInt($('#atb_calendar_container .cell.selection').length, 10);
	if (numDaysSelected > 0) {
		clearSelection();
		$('#cal_' + startSelect).addClass('selection');
	}
	
	// Ignore repeat user clicks while another request is active. The missing-date preset runs
	// during page startup, when unrelated initialisation requests may still be in flight, so its
	// programmatic selection must be allowed to set the arrival date and period.
	if (isAjaxRunning() && autoPeriod <= 0) {
		return;
	}

	InteractionBlocker.show();
	let csrfToken = $('#atb-csrf').val();
	$.ajax({
		type: 'POST',
		url: '/checkout/set/arrival/',
		dataType: 'json',
		data: {
			arrival_date: arrivalDate,
			anytime_booking_csrf: csrfToken
		}
	}).done(function(returnData) {
		const { short_arrival_date: shortArrivalDate, days } = returnData;

		$('#atb-config').attr('data-arrival_date_full', arrivalDate);
		$('#atb-summary-arrival-date').html(shortArrivalDate);

		// Update the calendar button arrival text
		$('#atb-calendar-btn #atb-calendar-btn-arrival-text').text(shortArrivalDate);
		$('#atb-calendar-btn #atb-calendar-btn-departure-text').text('');
		$('#atb-calendar-btn #atb-calendar-btn-period-text').text('');

		$('#atb-summary-arrival-row').show();

		// set up Length of stays
		let lastLos = 28;
		let losAllowed = days;
		losAllowed.forEach((day) => {
			lastLos = day;
		});

		$.each(losAllowed, function (i, item) {
			if (item > 0 && item < max) {
				$('#cal_' + (startSelect + (item - 0))).addClass('good_los');
			}
		});

		$('.good_los').each(function () {
			$(this).addClass('start');
		});
		setupSecondClick();

		// Trigger the app callback
		calendar._triggerStart(shortArrivalDate);
		if (autoPeriod > 0)
			calendar._triggerEnd(autoPeriod);
	}).always(function() {
		InteractionBlocker.hide();
	});
}



// TODO: this is pre REFACTOR endDayClicked - bring back if we need to revert
// function endDayClicked(period = 0) {
// 	$('#atb-period').val(period);
// 	updateCalendarButtonPeriodText(period);	
// 	reset2();
	
// 	onPeriodChanged();

//     // Trigger the app callback
//     calendar._triggerEnd(period);
// }

function endDayClicked(period = 0) {
	reset2();
	
    // Trigger the app callback
    calendar._triggerEnd(period);
}
